import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, CircularProgress } from '@mui/material';
import notificationService from '../services/notificationService';
import { NotificationDTO } from '../types/notification.types';

const NotificationDropdown: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Get user ID from localStorage
      const user = localStorage.getItem('user');
      if (user) {
        const parsedUser = JSON.parse(user);
        const userId = parsedUser.id;
        if (userId) {
          const unreadNotifications = await notificationService.getUnreadNotifications(userId);
          setNotifications(unreadNotifications.slice(0, 5)); // Show only latest 5
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now.getTime() - notifDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <Box sx={{ width: 320, maxHeight: 300, overflowY: 'auto', p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Notifications</Typography>
      <Divider sx={{ mb: 1 }} />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : notifications.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No new notifications
        </Typography>
      ) : (
        <List>
          {notifications.map((notif) => (
            <ListItem key={notif.id} alignItems="flex-start" sx={{ py: 1 }}>
              <ListItemText
                primary={notif.message}
                secondary={notif.createdAt ? getTimeAgo(notif.createdAt) : 'Recently'}
                primaryTypographyProps={{ fontSize: '1rem' }}
                secondaryTypographyProps={{ fontSize: '0.85rem', color: 'text.secondary' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default NotificationDropdown;
